import type { NoiseProfile, OnsetEvent, PitchReading } from '@/types/audio';

/** Messages the main thread sends to the analysis worker. */
export type ToWorker =
  | { type: 'init'; sampleRate: number; hopSize: number; sink: MessagePort }
  | { type: 'calibrate'; durationSec: number }
  | { type: 'setTarget'; chordId: string | null }
  | { type: 'setStringAnalysis'; enabled: boolean }
  | { type: 'setTuner'; enabled: boolean }
  | { type: 'reset' };

/** Messages the analysis worker sends back to the main thread. */
export type FromWorker =
  | { type: 'ready' }
  | { type: 'onset'; onset: OnsetEvent }
  | { type: 'frame'; frame: SerializableFrame }
  | { type: 'pitch'; pitch: PitchReading }
  | { type: 'calibrated'; profile: SerializableNoiseProfile }
  | { type: 'error'; message: string };

/** Structured-clone friendly noise profile (no methods, transferable buffers). */
export interface SerializableNoiseProfile {
  floorRms: number;
  floorDb: number;
  spectrum: Float32Array;
  clipRatio: number;
  headroomDb: number;
}

export interface SerializableStringReport {
  string: number;
  state: string;
  confidence: number;
  snrDb: number;
  usablePartials: number;
}

export interface SerializableFrame {
  time: number;
  chroma: Float32Array;
  rms: number;
  snrDb: number;
  tonalness: number;
  clipped: boolean;
  /** Hop-rate RMS values since the previous frame, for the level meter. */
  levels: Float32Array;
  /** The guitar presence gate's verdict about this frame's sound source. */
  source?: { kind: 'guitar' | 'non-guitar' | 'uncertain'; guitarConfidence: number };
  /** Present only while a target chord is set. */
  bass?: { expectedDb: number; unwantedDb: number };
  /** Present only while a target chord is set. */
  noteCoverage?: number;
  /** Present only while string analysis is enabled. */
  strings?: SerializableStringReport[];
}

export function toNoiseProfile(serialized: SerializableNoiseProfile): NoiseProfile {
  return {
    floorRms: serialized.floorRms,
    floorDb: serialized.floorDb,
    spectrum: serialized.spectrum,
    clipRatio: serialized.clipRatio,
    headroomDb: serialized.headroomDb,
  };
}
